import { useState } from 'react';
import { useGT } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { badgeVariants } from '@/components/ui/badge';
import {
  Sheet,
  SheetContentResizable,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { InvoiceDetailSlidein } from '@/components/accounts-payable/invoice-detail/invoice-detail-slidein';
import { PurchaseOrderDetailSlidein } from '@/components/accounts-payable/purchase-order-detail/purchase-order-detail-slidein';
import { InboundEmailDetailSheet } from '@/components/accounts-receivable/collection-threads/InboundEmailDetailSheet';
import { SystemEntityPreview } from '@/components/system-entities';
import { SystemEntityLink } from '@/components/system-entities/system-entity-link';
import {
  getEntityDisplayName,
  systemEntityIcons,
} from '@/components/system-entities/utils';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';
import { useLocalStorageValue } from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { relatedEntityLabel } from './relatedEntityLabel';

/**
 * Clickable "Related to" badge on task detail. Opens the entity in a slideout
 * (full invoice / email / PO view) instead of the hover-card summary.
 */
export function TaskRelatedEntityBadge({
  entityType,
  entityId,
}: {
  entityType: SystemEntity;
  entityId: string;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  // A module-level icon, looked up (not created) per render.
  const Icon = systemEntityIcons[entityType];
  const label = useRelatedEntityLabel(entityType, entityId);
  const typeName = getEntityDisplayName(entityType);
  const badgeClassName = cn(
    badgeVariants({ variant: 'outline' }),
    'max-w-xs cursor-pointer gap-1.5 hover:bg-accent',
  );

  // A Workbook has its own page and no slide-out: link straight to it.
  if (entityType === SystemEntity.WORKBOOK)
    return (
      <SystemEntityLink
        entityType={entityType}
        entityId={entityId}
        className={badgeClassName}
      >
        <Icon className="size-3 shrink-0" />
        <span className="truncate">{label}</span>
      </SystemEntityLink>
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={badgeClassName}
        aria-label={gt('Open {name}', {
          context: 'opens the related business record in a slide-out panel',
          name: label,
        })}
      >
        <Icon className="size-3 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      <TaskAnchorEntitySlidein
        entityType={entityType}
        entityId={entityId}
        title={typeName}
        isOpen={open}
        onOpenChange={setOpen}
      />
    </>
  );
}

function useRelatedEntityLabel(
  entityType: SystemEntity,
  entityId: string,
): string {
  const typeName = getEntityDisplayName(entityType);
  const { invoice: invoiceResponse, isLoading: invoiceLoading } = useAPInvoice(
    entityType === SystemEntity.AP_INVOICE ? entityId : undefined,
    entityType === SystemEntity.AP_INVOICE,
  );
  const { data: inboundEmail, isLoading: emailLoading } = useGetInboundEmail({
    inboundEmailId: entityId,
    enabled: entityType === SystemEntity.INBOUND_EMAIL,
  });
  // A Workbook is private to its Owner: anyone else sees the type name.
  const workbook = useQuery(
    orpc.workbooks.get.queryOptions({
      input: { workbookId: entityId },
      enabled: entityType === SystemEntity.WORKBOOK,
      retry: false,
    }),
  );
  return relatedEntityLabel(entityType, typeName, {
    invoiceLoading,
    invoiceNumber: invoiceResponse?.invoice?.invoiceNumber,
    emailLoading,
    emailSubject: inboundEmail?.subject,
    workbookName: workbook.data?.name,
  });
}

function TaskAnchorEntitySlidein({
  entityType,
  entityId,
  title,
  isOpen,
  onOpenChange,
}: {
  entityType: SystemEntity;
  entityId: string;
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { value: previewPanelSize, set: setPreviewPanelSize } =
    useLocalStorageValue<number>('task-anchor-preview-panel-size', {
      defaultValue: 50,
    });

  if (entityType === SystemEntity.AP_INVOICE) {
    return (
      <InvoiceDetailSlidein
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        invoiceId={entityId}
      />
    );
  }

  if (entityType === SystemEntity.INTERNAL_PURCHASE_ORDER) {
    return (
      <PurchaseOrderDetailSlidein
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        purchaseOrderId={entityId}
      />
    );
  }

  if (entityType === SystemEntity.INBOUND_EMAIL) {
    return (
      <InboundEmailDetailSheet
        inboundEmailId={isOpen ? entityId : null}
        onClose={() => onOpenChange(false)}
      />
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: previewPanelSize,
          onResize: setPreviewPanelSize,
          className: 'flex flex-col',
        }}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pt-3">
          <SystemEntityPreview
            entityType={entityType}
            entityId={entityId}
            className="!w-full !max-w-none !min-w-0"
          />
        </div>
      </SheetContentResizable>
    </Sheet>
  );
}
